import { CHANNELS } from '../../../src/ipc/channels';
import { requireUserId } from '../../../src/services/supabase/auth';
import { getCompanyMeetingIds } from '../../../src/services/supabase/directoryRepo';
import { t, uiLocale } from '../../../src/services/language';
import type { ExportProgressDTO, ExportResultDTO, TranscriptSegmentDTO } from '../../../src/shared/types';
import { downloadBlob, ZipBuilder } from '../zip';
import type { PageWebContents } from './mainWindow';
import { readRecording, safeRecordingPath } from './recordings';
import { getBlob, getJson, isS3Configured } from './s3';
import { embeddedSegments, openMeetingRows } from '../../../src/services/crypto/openContent';
import { getSupabase } from './supabaseClient';

/**
 * `archiveExport.ts`'nin web karşılığı. Masaüstünde seçilen klasöre toplantı
 * başına bir klasör yazılıyor; web'de aynı düzen tek bir ZIP dosyası olarak
 * iniyor:
 *   2026-09-18 Haftalık Toplantı/summary.md · transcript.txt · transcript.json · audio.mp3
 */

interface ExportMeetingRow {
  id: string;
  title: string;
  recorded_at: string | null;
  created_at: string;
  summary: string | null;
  key_points: unknown;
  options: any;
  action_items: Array<{ text: string; assignee: string | null; is_done: boolean; position: number }>;
  transcripts: Array<{ segments: unknown }> | null;
}

const strings = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];

const INVALID_FILENAME_CHARS = /[<>:"/\\|?*\x00-\x1F]/g;

function safeName(value: string): string {
  return value.replace(INVALID_FILENAME_CHARS, ' ').replace(/\s+/g, ' ').trim().slice(0, 80) || t('Toplantı', 'Meeting');
}

function summaryMarkdown(row: ExportMeetingRow): string {
  const decisions = strings(row.options?.decisions);
  const keyPoints = strings(row.key_points);
  const items = [...(row.action_items ?? [])].sort((a, b) => a.position - b.position);
  const when = new Date(row.recorded_at ?? row.created_at).toLocaleString(uiLocale());
  return [
    `# ${row.title}`,
    '',
    `_${when}_`,
    '',
    t('## Özet', '## Summary'),
    row.summary || '—',
    ...(keyPoints.length ? ['', t('## Ana Noktalar', '## Key points'), ...keyPoints.map((p) => `- ${p}`)] : []),
    ...(decisions.length ? ['', t('## Kararlar', '## Decisions'), ...decisions.map((d) => `- ${d}`)] : []),
    ...(items.length
      ? ['', t('## Aksiyonlar', '## Action items'), ...items.map((a) => `- [${a.is_done ? 'x' : ' '}] ${a.text}${a.assignee ? ` (${a.assignee})` : ''}`)]
      : []),
    '',
  ].join('\n');
}

async function loadTranscript(row: ExportMeetingRow): Promise<TranscriptSegmentDTO[]> {
  const key = row.options?.s3?.transcriptKey as string | undefined;
  if (key) return isS3Configured() ? getJson<TranscriptSegmentDTO[]>(key).catch(() => []) : [];
  const segments = embeddedSegments(row.transcripts);
  return Array.isArray(segments) ? (segments as TranscriptSegmentDTO[]) : [];
}

async function exportMeeting(row: ExportMeetingRow, zip: ZipBuilder, used: Set<string>): Promise<void> {
  const date = (row.recorded_at ?? row.created_at).slice(0, 10);
  let name = safeName(`${date} ${row.title}`);
  for (let i = 2; used.has(name.toLowerCase()); i++) name = safeName(`${date} ${row.title} (${i})`);
  used.add(name.toLowerCase());

  await zip.add(`${name}/summary.md`, summaryMarkdown(row));
  const transcript = await loadTranscript(row);
  if (transcript.length > 0) {
    await zip.add(`${name}/transcript.json`, JSON.stringify(transcript, null, 2));
    await zip.add(`${name}/transcript.txt`, transcript.map((s) => `[${s.timestamp}] ${s.speaker}: ${s.text}`).join('\n'));
  }

  const audioKey = row.options?.s3?.audioKey as string | undefined;
  const localAudio = await safeRecordingPath(row.options?.audioFilePath as string | undefined);
  if (audioKey && isS3Configured()) {
    await zip.add(`${name}/audio.mp3`, await getBlob(audioKey));
  } else if (localAudio) {
    const file = await readRecording(localAudio).catch(() => null);
    if (file) await zip.add(`${name}/audio.webm`, file.blob);
  }
}

let lastExportName: string | null = null;

export function getLastExportFolder(): string | null {
  return lastExportName;
}

export async function exportArchive(sender: PageWebContents, companyId?: string): Promise<ExportResultDTO> {
  const userId = await requireUserId();
  let query = getSupabase()
    .from('meetings')
    .select(`
      id, title, recorded_at, created_at, summary, key_points, options,
      action_items ( text, assignee, is_done, position ),
      transcripts ( segments )
    `)
    .eq('user_id', userId)
    .is('deleted_at', null)
    .neq('status', 'queued')
    .order('recorded_at', { ascending: false, nullsFirst: false });

  const fileName = `TalkNote-arsiv-${new Date().toISOString().slice(0, 10)}.zip`;
  if (companyId) {
    const ids = await getCompanyMeetingIds(companyId);
    if (ids.length === 0) return { ok: true, folder: fileName, exported: 0, failed: 0 };
    query = query.in('id', ids);
  }

  const { data, error } = await query;
  if (error) throw new Error(t(`Toplantılar okunamadı: ${error.message}`, `Could not read the meetings: ${error.message}`));
  // Arşiv kullanıcının kendisi için: şifreli alanlar (tn1) açılıp düz metin olarak yazılır.
  const rows = (await openMeetingRows((data ?? []) as unknown as ExportMeetingRow[], userId)) as ExportMeetingRow[];

  const zip = new ZipBuilder();
  const used = new Set<string>();
  let exported = 0;
  let failed = 0;
  const progress = (payload: ExportProgressDTO) => sender.send(CHANNELS.exportProgress, payload);

  for (const [index, row] of rows.entries()) {
    progress({ done: index, total: rows.length, current: row.title });
    try {
      await exportMeeting(row, zip, used);
      exported += 1;
    } catch (err) {
      failed += 1;
      console.error('[export] toplantı aktarılamadı:', row.id, err);
    }
  }
  progress({ done: rows.length, total: rows.length });
  downloadBlob(zip.toBlob(), fileName);
  lastExportName = fileName;
  return { ok: true, folder: fileName, exported, failed };
}
